import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Divider,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CalendarToday,
  Add,
  Delete,
  Save,
  Refresh,
  Settings
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';


// Import API methods and utilities
import { 
  diaryAPI, 
  handleAPIError, 
  formatters,
  diaryAuthUtils 
} from './api';

const WorkingDaysConfig = () => {
  const theme = useTheme();
  // Default working days (Monday to Friday = 1-5, Sunday = 0, Saturday = 6)
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  
  // Known Kenyan public holidays for 2025
  const [publicHolidays, setPublicHolidays] = useState([
    { date: '2025-01-01', name: 'New Year\'s Day', type: 'fixed' },
    { date: '2025-04-18', name: 'Good Friday', type: 'fixed' },
    { date: '2025-04-21', name: 'Easter Monday', type: 'fixed' },
    { date: '2025-05-01', name: 'Labour Day', type: 'fixed' },
    { date: '2025-06-01', name: 'Madaraka Day', type: 'fixed' },
    { date: '2025-10-10', name: 'Huduma Day', type: 'fixed' },
    { date: '2025-10-20', name: 'Mashujaa Day', type: 'fixed' },
    { date: '2025-12-12', name: 'Jamhuri Day', type: 'fixed' },
    { date: '2025-12-25', name: 'Christmas Day', type: 'fixed' },
    { date: '2025-12-26', name: 'Boxing Day', type: 'fixed' },
  ]);

  // Custom non-working days (government declarations, company closures)
  const [customNonWorkingDays, setCustomNonWorkingDays] = useState([]);
  
  // Custom working days (overrides for normally non-working days)
  const [customWorkingDays, setCustomWorkingDays] = useState([]);
  
  // Form states
  const [newNonWorkingDate, setNewNonWorkingDate] = useState('');
  const [newNonWorkingReason, setNewNonWorkingReason] = useState('');
  const [newWorkingDate, setNewWorkingDate] = useState('');
  const [newWorkingReason, setNewWorkingReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [configId, setConfigId] = useState(null);

  // Check authentication on component mount
  useEffect(() => {
    if (!diaryAuthUtils.isAuthenticated()) {
      setMessage({ 
        type: 'error', 
        text: 'Authentication required. Please log in to continue.' 
      });
      return;
    }
    
    loadFullConfiguration();
  }, []);

  const loadFullConfiguration = async () => {
    setLoading(true);
    try {
      // First, try to load the complete configuration
      const fullConfig = await diaryAPI.workingDays.loadFullConfiguration();
      
      if (fullConfig) {
        setWorkingDays(fullConfig.regular_working_days || [1, 2, 3, 4, 5]);
        
        if (fullConfig.public_holidays && fullConfig.public_holidays.length > 0) {
          setPublicHolidays(fullConfig.public_holidays);
        }
        
        setCustomNonWorkingDays(fullConfig.custom_non_working_days || []);
        setCustomWorkingDays(fullConfig.custom_working_days || []);
        
        setMessage({ type: 'success', text: 'Configuration loaded successfully!' });
      } else {
        // If no configuration exists, try to initialize public holidays
        await initializePublicHolidays();
      }
    } catch (error) {
      console.error('Error loading full configuration:', error);
      // Fallback to loading individual components
      await loadIndividualComponents();
    } finally {
      setLoading(false);
    }
  };

  const loadIndividualComponents = async () => {
    try {
      // Load basic configuration
      const configs = await diaryAPI.workingDays.getConfiguration();
      const config = configs.length > 0 ? configs[0] : null;
      
      if (config) {
        setWorkingDays(config.regular_working_days || [1, 2, 3, 4, 5]);
        setConfigId(config.id);
      }

      // Load public holidays
      const holidays = await diaryAPI.workingDays.getPublicHolidays(2025);
      if (holidays.length > 0) {
        setPublicHolidays(holidays);
      } else {
        // Initialize with default holidays if none exist
        await initializePublicHolidays();
      }

      // Load custom days
      const customDays = await diaryAPI.workingDays.getCustomWorkingDays();
      
      const workingDays = customDays.filter(day => day.day_type === 'working');
      const nonWorkingDays = customDays.filter(day => day.day_type === 'non_working');
      
      setCustomWorkingDays(workingDays.map(day => ({
        id: day.id,
        date: day.date,
        reason: day.reason,
        type: 'override'
      })));
      
      setCustomNonWorkingDays(nonWorkingDays.map(day => ({
        id: day.id,
        date: day.date,
        reason: day.reason,
        type: 'custom'
      })));

    } catch (error) {
      const errorMessage = handleAPIError(error);
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const initializePublicHolidays = async () => {
    try {
      const holidaysToCreate = publicHolidays.map(holiday => ({
        date: holiday.date,
        name: holiday.name,
        holiday_type: holiday.type,
        country: 'Kenya',
        is_active: true
      }));

      await diaryAPI.workingDays.bulkCreatePublicHolidays(holidaysToCreate);
      console.log('Public holidays initialized successfully');
    } catch (error) {
      console.log('Could not initialize public holidays:', error);
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  const toggleWorkingDay = (dayIndex) => {
    setWorkingDays(prev => 
      prev.includes(dayIndex) 
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex].sort()
    );
  };

  const addCustomNonWorkingDay = async () => {
    if (newNonWorkingDate && newNonWorkingReason.trim()) {
      try {
        const newDay = await diaryAPI.workingDays.createCustomWorkingDay({
          date: newNonWorkingDate,
          reason: newNonWorkingReason.trim(),
          is_working_day: false
        });

        setCustomNonWorkingDays(prev => [...prev, {
          id: newDay.id,
          date: newDay.date,
          reason: newDay.reason,
          type: 'custom'
        }].sort((a, b) => a.date.localeCompare(b.date)));
        
        setNewNonWorkingDate('');
        setNewNonWorkingReason('');
        setMessage({ type: 'success', text: 'Non-working day added successfully!' });
      } catch (error) {
        const errorMessage = handleAPIError(error);
        setMessage({ type: 'error', text: errorMessage });
      }
    }
  };

  const addCustomWorkingDay = async () => {
    if (newWorkingDate && newWorkingReason.trim()) {
      try {
        const newDay = await diaryAPI.workingDays.createCustomWorkingDay({
          date: newWorkingDate,
          reason: newWorkingReason.trim(),
          is_working_day: true
        });

        setCustomWorkingDays(prev => [...prev, {
          id: newDay.id,
          date: newDay.date,
          reason: newDay.reason,
          type: 'override'
        }].sort((a, b) => a.date.localeCompare(b.date)));
        
        setNewWorkingDate('');
        setNewWorkingReason('');
        setMessage({ type: 'success', text: 'Working day override added successfully!' });
      } catch (error) {
        const errorMessage = handleAPIError(error);
        setMessage({ type: 'error', text: errorMessage });
      }
    }
  };

  const removeCustomDay = async (id, type) => {
    try {
      await diaryAPI.workingDays.deleteCustomWorkingDay(id);

      if (type === 'non-working') {
        setCustomNonWorkingDays(prev => prev.filter(day => day.id !== id));
      } else {
        setCustomWorkingDays(prev => prev.filter(day => day.id !== id));
      }
      setMessage({ type: 'success', text: 'Custom day removed successfully!' });
    } catch (error) {
      const errorMessage = handleAPIError(error);
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const saveConfiguration = async () => {
    setLoading(true);
    try {
      // Prepare the complete configuration data
      const fullConfigData = {
        regular_working_days: workingDays,
        custom_non_working_days: customNonWorkingDays,
        custom_working_days: customWorkingDays
      };

      // Try to save using the full configuration endpoint
      try {
        await diaryAPI.workingDays.saveFullConfiguration(fullConfigData);
        setMessage({ type: 'success', text: 'Complete configuration saved successfully!' });
      } catch (error) {
        console.log('Full config save failed, trying individual save:', error);
        
        // Fallback to individual save
        const configData = {
          name: 'Default Working Days Configuration',
          working_days: workingDays,
          description: 'Main working days configuration for the organization'
        };

        if (configId) {
          await diaryAPI.workingDays.updateConfiguration(configId, configData);
        } else {
          const newConfig = await diaryAPI.workingDays.createConfiguration(configData);
          setConfigId(newConfig.id);
        }
        
        setMessage({ type: 'success', text: 'Working days configuration saved successfully!' });
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    setWorkingDays([1, 2, 3, 4, 5]);
    setCustomNonWorkingDays([]);
    setCustomWorkingDays([]);
    setMessage({ type: 'info', text: 'Configuration reset to defaults' });
  };

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Show authentication error if not authenticated
  if (!diaryAuthUtils.isAuthenticated()) {
    return (
      <Box sx={{ maxWidth: '800px', mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Authentication required. Please log in to access working days configuration.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: '1200px', 
      mx: 'auto', 
      p: 3,
      pb: { xs: 12, sm: 10, md: 8 } // Add extra bottom padding
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Settings color="primary" />
          Working Days Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure which days are available for meeting scheduling
        </Typography>
      </Box>

      {/* Message Alert */}
      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Regular Working Days */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Regular Working Days
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which days of the week are normally working days
          </Typography>
          
          <Grid container spacing={1}>
            {dayNames.map((day, index) => (
              <Grid item xs={12/7} key={index}>
                <Button
                  onClick={() => toggleWorkingDay(index)}
                  variant={workingDays.includes(index) ? 'contained' : 'outlined'}
                  sx={{
                    width: '100%',
                    minHeight: '60px',
                    flexDirection: 'column',
                    fontSize: '0.75rem',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {day.slice(0, 3)}
                  </Typography>
                  <Typography variant="caption">
                    {day}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Public Holidays */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Kenyan Public Holidays 2025
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These are automatically excluded from meeting scheduling
          </Typography>
          
          <Grid container spacing={2}>
            {publicHolidays.map((holiday, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper sx={{ p: 2, backgroundColor: theme.palette.background.default, }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {holiday.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatters.formatWorkingDate(holiday.date)}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: '50%' }} />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Custom Non-Working Days */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Additional Non-Working Days
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add special non-working days (government declarations, early closures, etc.)
          </Typography>
          
          <Paper sx={{ p: 2, mb: 2, backgroundColor: theme.palette.background.default }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  type="date"
                  value={newNonWorkingDate}
                  onChange={(e) => setNewNonWorkingDate(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  placeholder="Reason (e.g., Government declaration, Early closure)"
                  value={newNonWorkingReason}
                  onChange={(e) => setNewNonWorkingReason(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  onClick={addCustomNonWorkingDay}
                  variant="contained"
                  startIcon={<Add />}
                  fullWidth
                  disabled={loading}
                  sx={{ bgcolor: 'warning.main', '&:hover': { bgcolor: 'warning.dark' } }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {customNonWorkingDays.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {customNonWorkingDays.map((day) => (
                <Paper key={day.id} sx={{ p: 2, backgroundColor: theme.palette.background.default }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {day.reason}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatters.formatWorkingDate(day.date)}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => removeCustomDay(day.id, 'non-working')}
                      color="error"
                      size="small"
                      disabled={loading}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Custom Working Days (Overrides) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Special Working Days
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Override normally non-working days (weekends, holidays) to make them available
          </Typography>
          
          <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.background.default }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  type="date"
                  value={newWorkingDate}
                  onChange={(e) => setNewWorkingDate(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  placeholder="Reason (e.g., Make-up day, Special event)"
                  value={newWorkingReason}
                  onChange={(e) => setNewWorkingReason(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  onClick={addCustomWorkingDay}
                  variant="contained"
                  startIcon={<Add />}
                  fullWidth
                  color="success"
                  disabled={loading}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {customWorkingDays.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {customWorkingDays.map((day) => (
                <Paper key={day.id} sx={{ p: 2, bgcolor: '#e8f5e8' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {day.reason}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatters.formatWorkingDate(day.date)}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => removeCustomDay(day.id, 'working')}
                      color="error"
                      size="small"
                      disabled={loading}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
        <Button
          onClick={resetToDefaults}
          startIcon={<Refresh />}
          variant="outlined"
          disabled={loading}
        >
          Reset to Defaults
        </Button>
        <Button
          onClick={loadFullConfiguration}
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          variant="outlined"
          disabled={loading}
        >
          Reload
        </Button>
        <Button
          onClick={saveConfiguration}
          startIcon={loading ? <CircularProgress size={16} /> : <Save />}
          variant="contained"
          disabled={loading}
        >
          Save Configuration
        </Button>
      </Box>

      {/* Configuration Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Regular Working Days:
              </Typography>
              <Typography variant="body2">
                {workingDays.length > 0 
                  ? workingDays.map(day => dayNames[day]).join(', ')
                  : 'None selected'
                }
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Custom Non-Working Days:
              </Typography>
              <Typography variant="body2">
                {customNonWorkingDays.length} days added
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Special Working Days:
              </Typography>
              <Typography variant="body2">
                {customWorkingDays.length} overrides added
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WorkingDaysConfig;